import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Lock, ArrowLeft, Loader2, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import api from '../api/client';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('La contrasenya ha de tenir mínim 6 caràcters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Les contrasenyes no coincideixen.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/auth/reset-password', { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err?.detail || err?.message || "Error restablint la contrasenya.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8f3f4] to-[#f0e8ea] flex items-center justify-center p-4">
        <Card className="border-0 shadow-xl max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Enllaç no vàlid</h2>
            <p className="text-sm text-gray-500 mb-4">L'enllaç de restabliment no és vàlid. Sol·licita'n un de nou.</p>
            <Link to="/forgot-password">
              <Button className="bg-[#7B2D3F] hover:bg-[#7B2D3F]/90">Sol·licitar nou enllaç</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8f3f4] to-[#f0e8ea] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Ho-SYS" className="h-20 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">Nova contrasenya</h1>
          <p className="text-gray-500 text-sm mt-1">Introdueix la teva nova contrasenya</p>
        </div>

        <Card className="border-0 shadow-xl">
          <CardContent className="pt-6">
            {success ? (
              <div className="text-center py-4" data-testid="reset-password-success">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                <h2 className="text-lg font-bold text-gray-900 mb-2">Contrasenya actualitzada!</h2>
                <p className="text-sm text-gray-500 mb-6">
                  Ja pots iniciar sessió amb la teva nova contrasenya.
                </p>
                <Link to="/login">
                  <Button className="bg-[#7B2D3F] hover:bg-[#7B2D3F]/90 gap-2" data-testid="go-to-login-btn">
                    Iniciar sessió
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="password">Nova contrasenya</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Mínim 6 caràcters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      required
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirmar contrasenya</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="Repeteix la contrasenya"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="reset-confirm-password-input"
                    />
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700" data-testid="reset-error">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#7B2D3F] hover:bg-[#7B2D3F]/90"
                  data-testid="reset-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Restablir contrasenya"
                  )}
                </Button>

                <p className="text-center text-sm text-gray-500 mt-4">
                  <Link to="/login" className="text-[#7B2D3F] font-medium hover:underline">
                    <ArrowLeft className="w-3 h-3 inline mr-1" />
                    Tornar a iniciar sessió
                  </Link>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
